import { getShopRateApi } from '@user-center/api/collection'
import { multiplication } from '@user-center/utils/common'
import { get } from 'lodash-es'
import { computed, reactive, ref } from 'vue'
import { useStore } from 'vuex'

export interface CurrencyRateItem {
  fromCurrencyCode?: string
  toCurrencyCode?: string
  exchangeRate?: number
  [key: string]: any
}

export const currencySymbolMap: Record<string, string> = {
  USD: 'US$',
  TWD: 'NT$',
  CNY: '¥',
  EUR: '€',
  GBP: '£',
  JPY: 'JP¥',
  HKD: 'HK$',
  KRW: '₩',
  SGD: 'S$',
}

function normalizeCurrencyCode(code: any): string {
  return String(code || '').trim().toUpperCase()
}

function parseCurrencyAmount(amount: string | number): number {
  if (typeof amount === 'string')
    return Number.parseFloat(amount)
  return Number(amount)
}

function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100
}

function buildRateCacheKey(fromCurrencyCode: string, toCurrencyCode: string): string {
  return `${fromCurrencyCode}->${toCurrencyCode}`
}

function normalizeRateItem(item: any): CurrencyRateItem | null {
  const fromCurrencyCode = normalizeCurrencyCode(item?.fromCurrencyCode)
  const toCurrencyCode = normalizeCurrencyCode(
    item?.toCurrencyCode || item?.payCurrencyCode || item?.currencyCode,
  )
  const exchangeRate = Number(item?.exchangeRate)

  if (!fromCurrencyCode || !toCurrencyCode || !Number.isFinite(exchangeRate) || exchangeRate <= 0)
    return null

  return {
    ...item,
    fromCurrencyCode,
    toCurrencyCode,
    exchangeRate,
  }
}

export function formatCurrencyAmount(amount: number, currencyCode: string): number {
  if (!Number.isFinite(amount))
    return 0

  if (normalizeCurrencyCode(currencyCode) === 'TWD')
    return Math.ceil(amount)

  return roundToTwoDecimals(amount)
}

export function formatCurrencyAmountText(amount: string | number, currencyCode: string): string {
  const formattedAmount = formatCurrencyAmount(parseCurrencyAmount(amount), currencyCode)

  if (!Number.isFinite(formattedAmount))
    return ''

  if (normalizeCurrencyCode(currencyCode) === 'TWD')
    return String(formattedAmount)

  return formattedAmount
    .toFixed(2)
    .replace(/\.00$/, '')
    .replace(/(\.\d)0$/, '$1')
}

export function useCurrencyRate() {
  const store = useStore()
  const currentCurrency = ref<Record<string, any>>({})
  const currencyList = ref<CurrencyRateItem[]>([])
  const rateCacheMap = reactive<Record<string, CurrencyRateItem | null>>({})

  const storeInfo = computed(() => get(store, 'state.app.storeInfoData', {}) || {})
  const dealerShopId = computed(() => {
    return String(
      get(storeInfo.value, 'id')
      || get(storeInfo.value, 'shopId')
      || (typeof window !== 'undefined' ? window.localStorage.getItem('shopId') : '')
      || '',
    ).trim()
  })
  const defaultCurrencyCode = computed(() => {
    return normalizeCurrencyCode(get(storeInfo.value, 'defaultCurrencyCode') || 'CNY') || 'CNY'
  })
  const currentCurrencyCode = computed(() => {
    return normalizeCurrencyCode(get(currentCurrency.value, 'toCurrencyCode') || defaultCurrencyCode.value) || defaultCurrencyCode.value
  })

  function rebuildCurrencyList() {
    currencyList.value = Object.values(rateCacheMap).filter(Boolean) as CurrencyRateItem[]
  }

  function setRateItem(item: any) {
    const normalizedItem = normalizeRateItem(item)
    if (!normalizedItem)
      return null

    rateCacheMap[buildRateCacheKey(normalizedItem.fromCurrencyCode || '', normalizedItem.toCurrencyCode || '')] = normalizedItem
    rebuildCurrencyList()
    return normalizedItem
  }

  function findRateItem(fromCurrencyCode: string, toCurrencyCode: string): CurrencyRateItem | null {
    const normalizedFromCurrencyCode = normalizeCurrencyCode(fromCurrencyCode)
    const normalizedToCurrencyCode = normalizeCurrencyCode(toCurrencyCode)
    const cacheKey = buildRateCacheKey(normalizedFromCurrencyCode, normalizedToCurrencyCode)

    if (Object.prototype.hasOwnProperty.call(rateCacheMap, cacheKey))
      return rateCacheMap[cacheKey]

    const foundItem = currencyList.value.find(item =>
      normalizeCurrencyCode(item?.fromCurrencyCode) === normalizedFromCurrencyCode
      && normalizeCurrencyCode(item?.toCurrencyCode) === normalizedToCurrencyCode,
    ) || null

    if (foundItem)
      rateCacheMap[cacheKey] = foundItem

    return foundItem
  }

  async function fetchRateItem(fromCurrencyCode: string, toCurrencyCode: string): Promise<CurrencyRateItem | null> {
    const normalizedFromCurrencyCode = normalizeCurrencyCode(fromCurrencyCode)
    const normalizedToCurrencyCode = normalizeCurrencyCode(toCurrencyCode)

    if (!normalizedFromCurrencyCode || !normalizedToCurrencyCode)
      return null

    const cacheKey = buildRateCacheKey(normalizedFromCurrencyCode, normalizedToCurrencyCode)
    const cachedItem = findRateItem(normalizedFromCurrencyCode, normalizedToCurrencyCode)
    if (cachedItem)
      return cachedItem

    if (Object.prototype.hasOwnProperty.call(rateCacheMap, cacheKey) && rateCacheMap[cacheKey] === null)
      return null

    try {
      const res: any = await getShopRateApi({
        param: {
          pageNo: 0,
          pageSize: 20,
          fromCurrencyCode: normalizedFromCurrencyCode,
          toCurrencyCode: normalizedToCurrencyCode,
          activeFlag: true,
          dealerShopId: dealerShopId.value || undefined,
        },
      })
      const detail = setRateItem(res?.data?.dataList?.[0])
      rateCacheMap[cacheKey] = detail
      rebuildCurrencyList()
      return detail
    }
    catch (error) {
      console.error(error)
      rateCacheMap[cacheKey] = null
      rebuildCurrencyList()
      return null
    }
  }

  function syncCurrentCurrency() {
    setRateItem({
      fromCurrencyCode: 'CNY',
      toCurrencyCode: 'CNY',
      exchangeRate: 1,
    })

    if (typeof window === 'undefined') {
      currentCurrency.value = {}
      return
    }

    try {
      currentCurrency.value = JSON.parse(window.localStorage.getItem('currentCurrency') || '{}') || {}
      setRateItem(currentCurrency.value)
    }
    catch (error) {
      console.warn(error)
      currentCurrency.value = {}
    }
  }

  function getCurrencySymbol(currencyCode = currentCurrencyCode.value): string {
    const normalizedCurrencyCode = normalizeCurrencyCode(currencyCode) || currentCurrencyCode.value

    if (normalizedCurrencyCode === currentCurrencyCode.value) {
      return String(
        get(currentCurrency.value, 'symbol')
        || get(currentCurrency.value, 'shortCode')
        || currencySymbolMap[normalizedCurrencyCode]
        || normalizedCurrencyCode,
      )
    }

    return currencySymbolMap[normalizedCurrencyCode] || normalizedCurrencyCode
  }

  function getCurrencyRate(fromCurrencyCode: string, toCurrencyCode: string): number {
    const normalizedFromCurrencyCode = normalizeCurrencyCode(fromCurrencyCode)
    const normalizedToCurrencyCode = normalizeCurrencyCode(toCurrencyCode)

    if (!normalizedFromCurrencyCode || !normalizedToCurrencyCode)
      return 1

    if (normalizedFromCurrencyCode === normalizedToCurrencyCode)
      return 1

    const directRateItem = findRateItem(normalizedFromCurrencyCode, normalizedToCurrencyCode)
    if (directRateItem?.exchangeRate)
      return roundToTwoDecimals(Number(directRateItem.exchangeRate))

    const reverseRateItem = findRateItem(normalizedToCurrencyCode, normalizedFromCurrencyCode)
    if (reverseRateItem?.exchangeRate && Number(reverseRateItem.exchangeRate) > 0)
      return roundToTwoDecimals(1 / Number(reverseRateItem.exchangeRate))

    const fromToCny = findRateItem(normalizedFromCurrencyCode, 'CNY')
    const cnyToFrom = findRateItem('CNY', normalizedFromCurrencyCode)
    const cnyToTo = findRateItem('CNY', normalizedToCurrencyCode)
    const toToCny = findRateItem(normalizedToCurrencyCode, 'CNY')

    if (fromToCny?.exchangeRate && cnyToTo?.exchangeRate)
      return roundToTwoDecimals(Number(fromToCny.exchangeRate) * Number(cnyToTo.exchangeRate))

    if (cnyToFrom?.exchangeRate && cnyToTo?.exchangeRate && Number(cnyToFrom.exchangeRate) > 0)
      return roundToTwoDecimals(Number(cnyToTo.exchangeRate) / Number(cnyToFrom.exchangeRate))

    if (fromToCny?.exchangeRate && toToCny?.exchangeRate && Number(toToCny.exchangeRate) > 0)
      return roundToTwoDecimals(Number(fromToCny.exchangeRate) / Number(toToCny.exchangeRate))

    if (cnyToFrom?.exchangeRate && toToCny?.exchangeRate && Number(cnyToFrom.exchangeRate) > 0 && Number(toToCny.exchangeRate) > 0)
      return roundToTwoDecimals(1 / (Number(cnyToFrom.exchangeRate) * Number(toToCny.exchangeRate)))

    return 1
  }

  function hasCurrencyRate(fromCurrencyCode: string, toCurrencyCode: string): boolean {
    const normalizedFromCurrencyCode = normalizeCurrencyCode(fromCurrencyCode)
    const normalizedToCurrencyCode = normalizeCurrencyCode(toCurrencyCode)

    if (!normalizedFromCurrencyCode || !normalizedToCurrencyCode)
      return false

    if (normalizedFromCurrencyCode === normalizedToCurrencyCode)
      return true

    if (findRateItem(normalizedFromCurrencyCode, normalizedToCurrencyCode)?.exchangeRate)
      return true

    const reverseRate = findRateItem(normalizedToCurrencyCode, normalizedFromCurrencyCode)?.exchangeRate
    if (reverseRate && Number(reverseRate) > 0)
      return true

    const fromToCny = findRateItem(normalizedFromCurrencyCode, 'CNY')?.exchangeRate
    const cnyToFrom = findRateItem('CNY', normalizedFromCurrencyCode)?.exchangeRate
    const cnyToTo = findRateItem('CNY', normalizedToCurrencyCode)?.exchangeRate
    const toToCny = findRateItem(normalizedToCurrencyCode, 'CNY')?.exchangeRate

    if (fromToCny && cnyToTo)
      return true

    if (cnyToFrom && Number(cnyToFrom) > 0 && cnyToTo)
      return true

    if (fromToCny && toToCny && Number(toToCny) > 0)
      return true

    if (cnyToFrom && Number(cnyToFrom) > 0 && toToCny && Number(toToCny) > 0)
      return true

    return false
  }

  async function ensureCurrencyRate(fromCurrencyCode: string, toCurrencyCode: string): Promise<number> {
    const normalizedFromCurrencyCode = normalizeCurrencyCode(fromCurrencyCode)
    const normalizedToCurrencyCode = normalizeCurrencyCode(toCurrencyCode)

    if (!normalizedFromCurrencyCode || !normalizedToCurrencyCode || normalizedFromCurrencyCode === normalizedToCurrencyCode)
      return 1

    const pairList = [
      [normalizedFromCurrencyCode, normalizedToCurrencyCode],
      [normalizedToCurrencyCode, normalizedFromCurrencyCode],
    ]

    if (normalizedFromCurrencyCode !== 'CNY') {
      pairList.push(
        [normalizedFromCurrencyCode, 'CNY'],
        ['CNY', normalizedFromCurrencyCode],
      )
    }

    if (normalizedToCurrencyCode !== 'CNY') {
      pairList.push(
        ['CNY', normalizedToCurrencyCode],
        [normalizedToCurrencyCode, 'CNY'],
      )
    }

    const uniquePairList = Array.from(
      new Set(pairList.map(([fromCode, toCode]) => buildRateCacheKey(fromCode, toCode))),
    ).map((pairKey) => {
      const [fromCode, toCode] = pairKey.split('->')
      return [fromCode, toCode] as const
    })

    await Promise.all(uniquePairList.map(([fromCode, toCode]) => fetchRateItem(fromCode, toCode)))
    return getCurrencyRate(normalizedFromCurrencyCode, normalizedToCurrencyCode)
  }

  function convertAmount(amount: string | number): number {
    return convertCurrencyAmount('CNY', currentCurrencyCode.value, amount)
  }

  function convertOrgAmount(amount: string | number): number {
    const priceNum = parseCurrencyAmount(amount)
    if (!Number.isFinite(priceNum))
      return 0

    if (currentCurrencyCode.value === 'CNY')
      return priceNum

    const rate = getCurrencyRate('CNY', currentCurrencyCode.value)
    if (rate > 0)
      return Math.trunc((priceNum / rate) * 100) / 100

    return priceNum
  }

  function convertCurrencyAmount(fromCurrencyCode: string, toCurrencyCode: string, amount: string | number): number {
    const priceNum = parseCurrencyAmount(amount)
    const normalizedToCurrencyCode = normalizeCurrencyCode(toCurrencyCode)

    if (!Number.isFinite(priceNum))
      return 0

    if (normalizeCurrencyCode(fromCurrencyCode) === normalizedToCurrencyCode)
      return formatCurrencyAmount(priceNum, normalizedToCurrencyCode)

    const rate = getCurrencyRate(fromCurrencyCode, normalizedToCurrencyCode)
    return formatCurrencyAmount(Number(multiplication(priceNum, rate)), normalizedToCurrencyCode)
  }

  async function convertCurrencyAmountAsync(fromCurrencyCode: string, toCurrencyCode: string, amount: string | number): Promise<number> {
    await ensureCurrencyRate(fromCurrencyCode, toCurrencyCode)
    return convertCurrencyAmount(fromCurrencyCode, toCurrencyCode, amount)
  }

  syncCurrentCurrency()

  return {
    currentCurrency,
    currentCurrencyCode,
    currentCurrencySymbol: computed(() => getCurrencySymbol(currentCurrencyCode.value)),
    currencyList,
    defaultCurrencyCode,
    selectedCurrencyCode: currentCurrencyCode,
    syncCurrentCurrency,
    getCurrencySymbol,
    getCurrencyRate,
    hasCurrencyRate,
    ensureCurrencyRate,
    convertAmount,
    convertOrgAmount,
    convertCurrencyAmount,
    convertCurrencyAmountAsync,
    formatCurrencyAmount,
    formatCurrencyAmountText,
    currencySymbolMap,
  }
}
